import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useColors } from '@mastimanch/ui-components';
import { authService } from '../auth-config/auth-service';
import { useAuthContext } from '../auth-config';
import SecureVideoPlayer from '../components/SecureVideoPlayer';
import toast from 'react-hot-toast';
import { encryptId, decryptId } from '../utils/crypto';

import type { Chapter, Topic, BookInfo } from '../auth-config/types';

import { 
  FiChevronLeft, 
  FiChevronRight,
  FiShare2, 
  FiStar, 
  FiMoreVertical, 
  FiSearch, 
  FiClock, 
  FiGlobe, 
  FiSmartphone,
  FiChevronDown,
  FiChevronUp,
  FiX
} from 'react-icons/fi';

const CourseCatalogPage: React.FC = () => {
  const colors = useColors();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { bookId: pathBookId, videoId: pathVideoId } = useParams<{ bookId?: string; videoId?: string }>();
  
  const rawBookId = decryptId(searchParams.get('bookId') || pathBookId);
  const rawVideoId = decryptId(pathVideoId || searchParams.get('videoId'));

  const { signOut } = useAuthContext();
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [bookInfo, setBookInfo] = useState<BookInfo | null>(null);
  const [loading, setLoading] = useState(true);

  const handleSecurityViolation = async (bIdEnc: string, vIdEnc: string, tamperedVId: string) => {
    try {
      const token = await authService.getAccessToken();
      if (token) {
        await fetch('/api/student/security-violation', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ bookId: bIdEnc, videoId: vIdEnc, tamperedVideoId: tamperedVId })
        });
      }
    } catch (e) {
      console.error('Failed to report security violation', e);
    } finally {
      toast.error('Security Alert: Unauthorised action detected (URL tampering). Logging out...', { duration: 5000 });
      setTimeout(async () => {
        await signOut();
        navigate('/login');
      }, 3000);
    }
  };

  // Active topic/video states
  const [currentTopic, setCurrentTopic] = useState<Topic | null>(null);
  const [currentChapter, setCurrentChapter] = useState<Chapter | null>(null);

  // UI states
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [expandedAccordionChapters, setExpandedAccordionChapters] = useState<Set<number>>(new Set());
  const [completedLectures, setCompletedLectures] = useState<Set<number>>(new Set());
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isToggleHovered, setIsToggleHovered] = useState(false);

  useEffect(() => {
    const abortController = new AbortController();
    const fetchAll = async () => {
      try {
        setLoading(true);
        const token = await authService.getAccessToken();
        if (!token) {
          toast.error('Session expired. Please log in again.');
          return;
        }

        const chaptersUrl = rawBookId
          ? `/api/student/chapters?bookId=${encodeURIComponent(rawBookId)}`
          : '/api/student/chapters';

        const bookUrl = rawBookId
          ? `/api/student/books?bookId=${encodeURIComponent(rawBookId)}`
          : null;

        const headers = { 'Authorization': `Bearer ${token}` };
        const signal = abortController.signal;

        const [chaptersRes, bookRes, completedRes] = await Promise.all([
          fetch(chaptersUrl, { signal, headers }),
          bookUrl ? fetch(bookUrl, { signal, headers }) : Promise.resolve(null),
          fetch('/api/student/videos/completed', { signal, headers })
        ]);

        if (!chaptersRes.ok) throw new Error('Failed to load chapters.');
        const chaptersData: Chapter[] = await chaptersRes.json();
        setChapters(chaptersData);

        if (bookRes && bookRes.ok) {
          const bookData = await bookRes.json();
          setBookInfo(bookData?.[0] ?? null);
        }

        if (completedRes && completedRes.ok) {
          const completedIds: number[] = await completedRes.json();
          setCompletedLectures(new Set(completedIds));
        }

        // Set active topic/video
        if (chaptersData.length > 0) {
          let selectedTopic: Topic | null = null;
          let selectedChapter: Chapter | null = null;

          if (rawVideoId) {
            const parsedId = parseInt(rawVideoId, 10);
            for (const chap of chaptersData) {
              const topic = chap.topics.find(t => t.videoId === parsedId);
              if (topic) {
                selectedTopic = topic;
                selectedChapter = chap;
                break;
              }
            }
          }

          // Security Enforcement: If a video ID was requested but it doesn't belong to this book's chapters, flag it!
          if (rawVideoId && !selectedTopic) {
            const fallbackVidId = chaptersData[0]?.topics[0]?.videoId?.toString() || '0';
            handleSecurityViolation(rawBookId || '', fallbackVidId, pathVideoId || '');
            return;
          }

          // Fallback to first video in first chapter
          if (!selectedTopic) {
            const firstChap = chaptersData[0];
            if (firstChap.topics && firstChap.topics.length > 0) {
              selectedTopic = firstChap.topics[0];
              selectedChapter = firstChap;
            }
          }

          setCurrentTopic(selectedTopic);
          setCurrentChapter(selectedChapter);

          if (selectedChapter) {
            setExpandedAccordionChapters(new Set([selectedChapter.chapterId]));
          }
        }
      } catch (err: any) {
        if (err.name !== 'AbortError') {
          console.error(err);
          toast.error('Unable to load course content.');
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchAll();
    return () => abortController.abort();
  }, [rawBookId, rawVideoId]);

  const selectLecture = (topic: Topic, chapter: Chapter) => {
    setCurrentTopic(topic);
    setCurrentChapter(chapter);
    
    // Update path parameter to reflect current video without hard page reload
    const bId = encryptId(rawBookId || bookInfo?.id || '');
    const vId = encryptId(topic.videoId);
    if (bId) {
      navigate(`/courses/${bId}/${vId}`, { replace: true });
    } else {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('videoId', vId);
      setSearchParams(newParams);
    }
  };

  const toggleAccordionChapter = (chapterId: number) => {
    setExpandedAccordionChapters(prev => {
      const next = new Set(prev);
      if (next.has(chapterId)) {
        next.delete(chapterId);
      } else {
        next.add(chapterId);
      }
      return next;
    });
  };

  const toggleLectureCompleted = async (vidId: number) => {
    const willBeCompleted = !completedLectures.has(vidId);
    setCompletedLectures(prev => {
      const next = new Set(prev);
      if (next.has(vidId)) {
        next.delete(vidId);
      } else {
        next.add(vidId);
      }
      return next;
    });

    try {
      const token = await authService.getAccessToken();
      if (!token) return;
      await fetch(`/api/student/videos/${vidId}/progress`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ lastPositionSeconds: 0, durationSeconds: 0, isCompleted: willBeCompleted })
      });
    } catch (e) {
      console.error('Failed to save manual video completion toggle', e);
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins > 0) return `${mins}m ${secs > 0 ? secs + 's' : ''}`.trim();
    return `${seconds}s`;
  };

  const totalTopics = chapters.reduce((sum, c) => sum + c.topics.length, 0);
  const totalDuration = chapters.reduce(
    (sum, c) => sum + c.topics.reduce((s, t) => s + t.duration, 0),
    0
  );
  const totalMins = Math.round(totalDuration / 60);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[500px] space-y-4 bg-white text-gray-800">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-100 border-t-blue-600"></div>
        <p className="text-sm font-semibold text-gray-500">
          Loading curriculum course content...
        </p>
      </div>
    );
  }

  // Empty state if no chapters are published
  if (chapters.length === 0) {
    return (
      <div className="min-h-screen bg-white py-12 px-6">
        <div className="max-w-xl mx-auto rounded-2xl p-16 text-center border-2 border-dashed border-gray-300">
          <span className="text-6xl block mb-4">📚</span>
          <h3 className="text-xl font-bold mb-2 text-gray-900">
            No Published Lectures Yet
          </h3>
          <p className="text-sm max-w-sm mx-auto text-gray-500">
            There are no verified lectures for this book yet. Check back soon!
          </p>
          <button 
            onClick={() => navigate('/dashboard')}
            className="mt-6 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition"
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  // Get list of all topics sequentially to implement next/prev chevrons
  const allTopics = chapters.flatMap(c => c.topics);
  const currentTopicIndex = allTopics.findIndex(t => t.videoId === currentTopic?.videoId);
  const prevTopic = currentTopicIndex > 0 ? allTopics[currentTopicIndex - 1] : null;
  const nextTopic = currentTopicIndex < allTopics.length - 1 && currentTopicIndex !== -1 ? allTopics[currentTopicIndex + 1] : null;

  return (
    <div className="flex w-full min-h-screen bg-[#080b13] text-gray-100 overflow-x-hidden">
      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${isSidebarOpen ? 'mr-[350px]' : ''}`}>
      {/* Theatre Mode Header */}
      <div className="bg-[#0b0f19] px-6 py-4 flex items-center justify-between border-b border-gray-800/60">
        <div className="flex items-center space-x-4">
          <button 
            onMouseDown={(e) => {
              e.preventDefault();
              navigate('/dashboard');
            }}
            onClick={() => navigate('/dashboard')}
            className="relative z-50 pointer-events-auto flex items-center justify-center w-8 h-8 rounded-full bg-gray-800 hover:bg-gray-700 transition"
          >
            <FiChevronLeft size={20} className="text-gray-300" />
          </button>
          <div>
            <h1 className="text-sm font-extrabold tracking-tight text-white line-clamp-1">
              {bookInfo?.name || 'Curriculum Course'}
            </h1>
            {currentTopic && (
              <p className="text-xs text-gray-400 font-medium">
                Active Lecture: {currentTopic.topicName}
              </p>
            )}
          </div>
        </div>

        {/* Header Options */}
        <div className="flex items-center space-x-5 text-xs font-semibold text-gray-300">
          <button className="flex items-center space-x-1.5 hover:text-white transition">
            <FiStar size={14} className="text-yellow-500" />
            <span>Leave a rating</span>
          </button>
          
          <div className="flex items-center space-x-2">
            <div className="relative w-8 h-8 flex items-center justify-center rounded-full bg-gray-800">
              <svg className="w-8 h-8 transform -rotate-90">
                <circle cx="16" cy="16" r="14" fill="none" stroke="#374151" strokeWidth="2" />
                <circle 
                  cx="16" 
                  cy="16" 
                  r="14" 
                  fill="none" 
                  stroke="#3b82f6" 
                  strokeWidth="2" 
                  strokeDasharray="88" 
                  strokeDashoffset={88 - (88 * completedLectures.size) / (totalTopics || 1)} 
                />
              </svg>
              <span className="absolute text-[8px] font-bold text-white">
                {Math.round((completedLectures.size / (totalTopics || 1)) * 100)}%
              </span>
            </div>
            <span>Your progress</span>
          </div>
        </div>
      </div>

      {/* Video Container (Theatre Mode) */}
      <div className="w-full flex flex-col justify-start items-center bg-black py-2 relative">
        <div className="w-full max-w-5xl aspect-video overflow-hidden shadow-2xl relative">
          {prevTopic && (
            <button 
              onClick={() => selectLecture(prevTopic, chapters.find(c => c.topics.some(t => t.videoId === prevTopic.videoId)) || chapters[0])}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-purple-700 hover:bg-purple-800 text-white rounded-lg flex items-center justify-center transition cursor-pointer"
            >
              <FiChevronLeft size={24} />
            </button>
          )}
          {nextTopic && (
            <button 
              onClick={() => selectLecture(nextTopic, chapters.find(c => c.topics.some(t => t.videoId === nextTopic.videoId)) || chapters[0])}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-purple-700 hover:bg-purple-800 text-white rounded-lg flex items-center justify-center transition cursor-pointer"
            >
              <FiChevronRight size={24} />
            </button>
          )}
          {currentTopic && <SecureVideoPlayer videoId={currentTopic.videoId.toString()} bookInfo={bookInfo} />}
        </div>

        {/* Toggle Button */}
        {!isSidebarOpen && (
          <div className="absolute right-0 top-6 z-20">
            <button
              onMouseEnter={() => setIsToggleHovered(true)}
              onMouseLeave={() => setIsToggleHovered(false)}
              onClick={() => setIsSidebarOpen(true)}
              className="flex items-center bg-[#5624d0] hover:bg-[#401b9c] text-white font-bold text-xs py-2 px-3 rounded-l-md shadow-md transition-all duration-300 ease-in-out cursor-pointer h-10"
            >
              <FiChevronLeft size={16} />
              <span className={`overflow-hidden transition-all duration-300 ease-in-out whitespace-nowrap ${isToggleHovered ? 'max-w-[120px] ml-2 opacity-100' : 'max-w-0 opacity-0'}`}>
                Course content
              </span>
            </button>
          </div>
        )}
      </div>

      {/* Lower Part: White Content Area */}
      <div className="bg-white text-gray-800 flex-1 flex flex-col">
        {/* Tabs Bar */}
        <div className="border-b border-gray-200 bg-white">
          <div className="max-w-5xl mx-auto flex items-center">
            <div className="px-4 text-gray-400 cursor-pointer hover:text-gray-600">
              <FiSearch size={16} />
            </div>
            <div className="flex overflow-x-auto scrollbar-none">
              {[
                { id: 'content', label: 'Course content' },
                { id: 'overview', label: 'Overview' },
                { id: 'notes', label: 'Notes' },
                { id: 'announcements', label: 'Announcements' },
                { id: 'reviews', label: 'Reviews' },
                { id: 'tools', label: 'Learning tools' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-5 py-3.5 font-bold text-xs uppercase tracking-wider border-b-2 whitespace-nowrap transition-colors duration-150 ${
                    activeTab === tab.id
                      ? 'border-gray-900 text-gray-900'
                      : 'border-transparent text-gray-500 hover:text-gray-900'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Tab content area */}
        <div className="max-w-5xl w-full mx-auto px-6 py-8 flex-1">
          {activeTab === 'overview' && (
            <div className="space-y-6 animate-fadeIn">
              {/* Heading */}
              <h2 className="text-2xl font-bold text-gray-900 leading-tight">
                {bookInfo?.name || 'Masti Manch Course Catalog'}: Mastering {bookInfo?.subjectName || 'Curriculum'}
              </h2>

              {/* Description section */}
              <div className="text-sm text-gray-600 leading-relaxed font-semibold">
                <p>
                  This curriculum course is designed to empower students and learners to master the core concepts of {bookInfo?.subjectName || 'this subject'}. Whether you're studying for your board exams, preparing classroom homework, or simply interested in building a solid foundational logic, this course offers practical, easy-to-understand guidance.
                </p>
                {showFullDescription && (
                  <div className="mt-4 space-y-4 animate-fadeIn">
                    <p>
                      In this course, you will learn the exact topics recommended by standard academic curriculum guidelines, mapped with high-definition audio reviews and visual content.
                    </p>
                    <h5 className="font-bold text-gray-900">What you will learn:</h5>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>In-depth chapter analysis with sequential topic indexes.</li>
                      <li>Detailed explanation of complex theories with real-world examples.</li>
                      <li>Practice modules and self-evaluation guidelines.</li>
                    </ul>
                  </div>
                )}
                <button 
                  onClick={() => setShowFullDescription(!showFullDescription)}
                  className="mt-3 text-purple-700 hover:text-purple-900 font-bold flex items-center gap-1 text-xs"
                >
                  {showFullDescription ? (
                    <>Show less <FiChevronUp /></>
                  ) : (
                    <>Show more <FiChevronDown /></>
                  )}
                </button>
              </div>
            </div>
          )}

          {activeTab === 'content' && (
            <div className="space-y-4 animate-fadeIn">
              <div className="flex justify-between items-center text-sm text-gray-600 mb-2 font-semibold">
                <span>{chapters.length} sections • {totalTopics} lectures • {totalMins}m total length</span>
                <button 
                  onClick={() => {
                    if (expandedAccordionChapters.size === chapters.length) {
                      setExpandedAccordionChapters(new Set());
                    } else {
                      setExpandedAccordionChapters(new Set(chapters.map(c => c.chapterId)));
                    }
                  }}
                  className="text-purple-700 hover:text-purple-900 font-bold text-xs"
                >
                  {expandedAccordionChapters.size === chapters.length ? 'Collapse all sections' : 'Expand all sections'}
                </button>
              </div>

              <div className="border border-gray-200 rounded-lg overflow-hidden divide-y divide-gray-200">
                {chapters.map((chap, cIdx) => {
                  const isOpen = expandedAccordionChapters.has(chap.chapterId);
                  const chapterDuration = chap.topics.reduce((acc, t) => acc + t.duration, 0);
                  const completedInChapter = chap.topics.filter(t => completedLectures.has(t.videoId)).length;

                  return (
                    <div key={chap.chapterId} className="bg-white">
                      {/* Section Header */}
                      <button
                        onClick={() => toggleAccordionChapter(chap.chapterId)}
                        className="w-full flex items-center justify-between p-4 bg-[#f7f9fa] hover:bg-gray-100/80 transition text-left"
                      >
                        <div className="flex-1 pr-4">
                          <h4 className="text-sm font-bold text-gray-900 leading-tight">
                            Section {cIdx + 1}: {chap.chapterName}
                          </h4>
                          <p className="text-[11px] text-gray-500 mt-1 font-bold">
                            {completedInChapter} / {chap.topics.length} | {Math.round(chapterDuration / 60)}m
                          </p>
                        </div>
                        <span className="text-gray-500 text-xs">
                          {isOpen ? <FiChevronUp /> : <FiChevronDown />}
                        </span>
                      </button>

                      {/* Lectures list */}
                      {isOpen && (
                        <div className="divide-y divide-gray-100 bg-white">
                          {chap.topics.map((topic, tIdx) => {
                            const isPlaying = currentTopic && topic.videoId === currentTopic.videoId;
                            const isCompleted = completedLectures.has(topic.videoId);
                            return (
                              <div 
                                key={topic.videoId}
                                className={`flex items-start justify-between py-3 px-6 transition-colors ${
                                  isPlaying ? 'bg-purple-50/30 font-bold' : 'hover:bg-gray-50/40'
                                }`}
                              >
                                <div className="flex items-start space-x-4 flex-1 min-w-0">
                                  <input
                                    type="checkbox"
                                    checked={isCompleted}
                                    onChange={() => toggleLectureCompleted(topic.videoId)}
                                    className="mt-0.5 w-4.5 h-4.5 accent-purple-600 rounded bg-white border-gray-300 text-purple-600 focus:ring-0 cursor-pointer"
                                  />
                                  <button
                                    onClick={() => selectLecture(topic, chap)}
                                    className="text-left flex-1 min-w-0"
                                  >
                                    <p className={`text-xs font-bold leading-normal ${isPlaying ? 'text-purple-700' : 'text-gray-700 hover:text-gray-900'}`}>
                                      {tIdx + 1}. {topic.topicName}
                                    </p>
                                    <div className="flex items-center space-x-2 mt-1">
                                      <span className="text-[10px] text-gray-500 font-bold">▶ {formatDuration(topic.duration)}</span>
                                    </div>
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {activeTab === 'notes' && (
            <div className="text-center py-12 space-y-3 animate-fadeIn text-gray-700">
              <span className="text-4xl block">📝</span>
              <h4 className="text-sm font-bold text-gray-900">Create notes for your lectures</h4>
              <p className="text-xs text-gray-500 max-w-sm mx-auto font-semibold">
                Notes are a great way to keep track of important concepts. Type your note in the box below to save it for this lecture.
              </p>
              <div className="mt-4 max-w-lg mx-auto">
                <textarea 
                  placeholder="Add a new note..."
                  className="w-full bg-white border border-gray-300 rounded-lg p-3 text-xs text-gray-800 focus:outline-none focus:border-purple-500/50 resize-none h-20"
                />
                <button 
                  onMouseDown={(e) => {
                    e.preventDefault();
                    toast.error('This feature is not allowed in trial.');
                  }}
                  className="mt-2 px-4 py-2 bg-purple-700 hover:bg-purple-800 text-white rounded-lg text-xs font-bold transition"
                >
                  Save Note
                </button>
              </div>
            </div>
          )}

          {activeTab === 'announcements' && (
            <div className="text-center py-16 space-y-2 animate-fadeIn text-gray-700">
              <span className="text-4xl block">📢</span>
              <h4 className="text-sm font-bold text-gray-900 font-bold">No announcements yet</h4>
              <p className="text-xs text-gray-500 max-w-xs mx-auto font-semibold">
                The instructor hasn’t posted any announcements for this course yet.
              </p>
            </div>
          )}

          {activeTab === 'reviews' && (
            <div className="text-center py-16 space-y-2 animate-fadeIn text-gray-700">
              <span className="text-4xl block">⭐</span>
              <h4 className="text-sm font-bold text-gray-900">Student Reviews</h4>
              <p className="text-xs text-gray-500 max-w-xs mx-auto font-semibold">
                Be the first to leave a rating and share your learning feedback!
              </p>
            </div>
          )}

          {activeTab === 'tools' && (
            <div className="text-center py-16 space-y-2 animate-fadeIn text-gray-700">
              <span className="text-4xl block">🛠️</span>
              <h4 className="text-sm font-bold text-gray-900">Learning Tools</h4>
              <p className="text-xs text-gray-500 max-w-xs mx-auto font-semibold">
                Access calendar sync, push reminders, and download options to stay on track.
              </p>
            </div>
          )}
        </div>
      </div>
      </div>

      {/* Sidebar Panel */}
      {isSidebarOpen && (
        <div 
          className="w-[350px] bg-white text-gray-800 border-l border-gray-200 flex flex-col fixed right-0 overflow-y-auto z-40"
          style={{ height: 'calc(100vh - 64px)', top: '64px' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
            <h3 className="text-sm font-bold text-gray-900">Course content</h3>
            <button 
              onClick={() => setIsSidebarOpen(false)}
              className="text-gray-500 hover:text-gray-900 p-1 rounded transition cursor-pointer"
            >
              <FiX size={18} />
            </button>
          </div>

          {/* Accordion list */}
          <div className="divide-y divide-gray-200">
            {chapters.map((chap, cIdx) => {
              const isOpen = expandedAccordionChapters.has(chap.chapterId);
              const chapterDuration = chap.topics.reduce((acc, t) => acc + t.duration, 0);
              const completedInChapter = chap.topics.filter(t => completedLectures.has(t.videoId)).length;

              return (
                <div key={chap.chapterId} className="bg-white">
                  {/* Section Header */}
                  <button
                    onClick={() => toggleAccordionChapter(chap.chapterId)}
                    className="w-full flex items-center justify-between p-4 bg-[#f7f9fa] hover:bg-gray-100/80 transition text-left"
                  >
                    <div className="flex-1 pr-4">
                      <h4 className="text-xs font-bold text-gray-900 leading-tight">
                        Section {cIdx + 1}: {chap.chapterName}
                      </h4>
                      <p className="text-[10px] text-gray-500 mt-1 font-bold">
                        {completedInChapter} / {chap.topics.length} | {Math.round(chapterDuration / 60)}m
                      </p>
                    </div>
                    <span className="text-gray-500 text-xs">
                      {isOpen ? <FiChevronUp /> : <FiChevronDown />}
                    </span>
                  </button>

                  {/* Lectures list */}
                  {isOpen && (
                    <div className="divide-y divide-gray-100 bg-white">
                      {chap.topics.map((topic, tIdx) => {
                        const isPlaying = topic.videoId === currentTopic?.videoId;
                        const isCompleted = completedLectures.has(topic.videoId);
                        return (
                          <div 
                            key={topic.videoId}
                            className={`flex items-start justify-between py-3 px-4 transition-colors ${
                              isPlaying ? 'bg-[#d1d7dc]/50 font-bold' : 'hover:bg-gray-50/40'
                            }`}
                          >
                            <div className="flex items-start space-x-3 flex-1 min-w-0">
                              <input
                                type="checkbox"
                                checked={isCompleted}
                                onChange={() => toggleLectureCompleted(topic.videoId)}
                                className="mt-0.5 w-4 h-4 accent-purple-600 rounded bg-white border-gray-300 text-purple-600 focus:ring-0 cursor-pointer"
                              />
                              <button
                                onClick={() => selectLecture(topic, chap)}
                                className="text-left flex-1 min-w-0"
                              >
                                <p className={`text-xs leading-normal ${isPlaying ? 'text-purple-700 font-bold' : 'text-gray-700 hover:text-gray-900 font-semibold'}`}>
                                  {tIdx + 1}. {topic.topicName}
                                </p>
                                <div className="flex items-center space-x-2 mt-1">
                                  <span className="text-[10px] text-gray-500 font-medium">▶ {formatDuration(topic.duration)}</span>
                                </div>
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseCatalogPage;
